import { Component, OnInit, ElementRef, ViewChild, AfterViewInit, Input } from '@angular/core';
import { UserService } from '../../../services/user.service';
import { PowerBiEmbedResponse, PowerBiEmbedConfig } from 'src/types/client/clients';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import * as pbi from 'powerbi-client';

@Component({
  selector: 'app-client-reports',
  templateUrl: './client-reports.component.html',
  styleUrls: ['./client-reports.component.css']
})
export class ClientReportsComponent implements OnInit, AfterViewInit {
  @Input() clientID: string = '';
  @Input() clientEmail: string = '';
  @ViewChild('reportContainer') reportContainer!: ElementRef;
  
  hasError: boolean = false;
  errorMessage: string = '';
  isLoading: boolean = true;
  
  private powerbi = new pbi.service.Service(
    pbi.factories.hpmFactory,
    pbi.factories.wpmpFactory,
    pbi.factories.routerFactory
  );

  constructor(
    private userService: UserService,
    private errorHandler: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    console.log("email:-", this.clientEmail);
    this.loadPowerBiReport();
  }
  
  ngAfterViewInit(): void {
    // The container is available after view init
  }

  loadPowerBiReport(): void {
    this.isLoading = true;
    this.userService.getPowerBiEmbedConfig(this.clientEmail).subscribe({
      next: (response: PowerBiEmbedResponse) => 
      {
        if (response.isSuccess && response.data) 
        {
          // Wait for the view to be ready before embedding
          setTimeout(() => {
            this.embedReport(response.data.reportId, response.data.embedUrl, response.data.embedToken);
          }, 500);
        } else 
        {
          this.handleError('Failed to load report configuration');
        }
      },
      error: (error) => 
      {
        // Handle unauthorized errors
        this.errorHandler.handleUnauthorizedError(error);
        this.handleError('Failed to load report. Please try again later.');
      }
    });
  }
  
  embedReport(reportId: string, embedUrl: string, embedToken: string): void {
    if (!this.reportContainer) 
    {
      this.handleError('Failed to initialize report container');
      return;
    }
    
    try {
      // Clear any existing content
      const reportContainerElement = this.reportContainer.nativeElement;
      while (reportContainerElement.firstChild) {
        reportContainerElement.removeChild(reportContainerElement.firstChild);
      }
      
      // Configure the report embedding
      const config: pbi.IEmbedConfiguration = {
        type: 'report',
        tokenType: pbi.models.TokenType.Embed,
        accessToken: embedToken,
        embedUrl: embedUrl,
        id: reportId,
        permissions: pbi.models.Permissions.All,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: true,
          background: pbi.models.BackgroundType.Default,
          localeSettings: {
            formatLocale: navigator.language
          }
        }
      };
      
      // Embed the report
      const report = this.powerbi.embed(reportContainerElement, config) as pbi.Report;
      
      // Add event handlers
      report.on('loaded', () => 
      {
        this.isLoading = false;
      });
      
      report.on('error', (event) =>
      {
        const errorDetail = event.detail as { message?: string, status?: number } || {};
        
        // Check if it's an unauthorized error (status 401)
        if (errorDetail.status === 401) {
          this.errorHandler.handleUnauthorizedError({ status: 401 });
        }
        
        this.handleError(`Error loading report: ${errorDetail.message || 'Unknown error'}`);
      });
    } catch (error) 
    {
      this.handleError('Failed to embed report. Please try again later.');
    }
  }

  onIframeError() {
    this.handleError('Unable to load the report. Please make sure you have the necessary permissions.');
  }

  private handleError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.isLoading = false;
  }
}